import { t } from '../../../../services/i18nService';

/**
 * Extended Text Keyboard Service
 *
 * This service handles keyboard accessibility specifically for Extended Text (Essay) interactions.
 * It is designed to work with CKEditor rich text formatting toolbar and plain text areas.
 *
 * IMPORTANT NOTE FOR IIB ACCESSIBILITY:
 * This component is part of the Extended Text interaction accessibility implementation.
 * If this component becomes part of the IIB (Inspera Integrity Browser) accessibility framework
 * in future iterations, please ensure clear distinction from:
 * - ACE Code Editor toolbar accessibility (different toolbar system)
 * - Other player component accessibility implementations
 * - Global keyboard shortcut handlers
 *
 * The keyboard shortcuts handled here are specifically:
 * - Alt+F10: Access CKEditor formatting toolbar (WCAG standard)
 * - Escape: Return from CKEditor toolbar to editor content
 * - Ctrl+Enter: Submit rich text answers (CKEditor only)
 */

// Key constants for accessibility shortcuts
const KEYBOARD_KEYS = {
    F10: 'F10',
    ESCAPE: 'Escape',
    ENTER: 'Enter',
};

// Editor type constants
const EDITOR_TYPES = {
    PLAIN: 'plain',
    RICH: 'rich',
};

const extendedTextKeyboardService = {
    /**
     * Handles specific accessibility keyboard shortcuts for extended text interactions
     * Only handles non-standard shortcuts that don't interfere with normal text input
     * @param {KeyboardEvent} event - The keyboard event
     * @param {object} options - Options including callbacks
     */
    handleAccessibilityShortcuts(event, options = {}) {
        const { key, altKey, ctrlKey, shiftKey } = event;
        const { focusToolbarCallback, returnToEditorCallback, submitCallback } = options;

        // Only handle Alt+F10 for CKEditor toolbar access (standard accessibility shortcut)
        // This is the CKEditor formatting toolbar, not the ACE code editor toolbar
        if (altKey && key === KEYBOARD_KEYS.F10 && focusToolbarCallback) {
            event.preventDefault();
            focusToolbarCallback();
            this.announceAction(t('interactions.extendedText.toolbarFocused'));
            return true;
        }

        // Only handle Escape to return from CKEditor toolbar (when currently in toolbar context)
        if (key === KEYBOARD_KEYS.ESCAPE && returnToEditorCallback) {
            // Only if we're currently in a toolbar context
            const activeElement = document.activeElement;
            if (activeElement && activeElement.closest('[role="toolbar"], .cke_toolbar')) {
                event.preventDefault();
                returnToEditorCallback();
                this.announceAction(t('interactions.extendedText.editorFocused'));
                return true;
            }
        }

        // Handle Ctrl+Enter for rich text (CKEditor) submission
        if (key === KEYBOARD_KEYS.ENTER && ctrlKey && !altKey && !shiftKey && submitCallback) {
            const activeElement = document.activeElement;

            // For CKEditor - submit on Ctrl+Enter (preserve normal Enter for line breaks)
            const isCKEditor = activeElement && (
                activeElement.contentEditable === 'true' ||
                activeElement.closest('.cke_wysiwyg_frame, .cke_contents')
            );

            if (isCKEditor && ctrlKey && !shiftKey) {
                event.preventDefault();
                submitCallback();
                this.announceAction(t('interactions.extendedText.answerSubmitted'));
                return true;
            }
        }

        return false;
    },

    /**
     * Gets the keyboard shortcuts description for screen readers
     * @param {string} editorType - Type of editor ('plain' or 'rich')
     * @returns {string} Formatted keyboard shortcuts description
     */
    getKeyboardShortcutsDescription(editorType = EDITOR_TYPES.PLAIN) {
        if (editorType === EDITOR_TYPES.PLAIN) {
            return t('interactions.extendedTextPlain.helpInstructions');
        }

        // Rich text editor uses the main extendedText keyboard instructions
        return t('interactions.extendedText.keyboardInstructions');
    },

    /**
     * Announces keyboard action to screen readers
     * @param {string} action - The action that was performed
     */
    announceAction(action) {
        // Create an aria-live region for announcements
        const existingRegion = document.getElementById('extended-text-announcements');
        let liveRegion = existingRegion;

        if (!liveRegion) {
            liveRegion = document.createElement('div');
            liveRegion.id = 'extended-text-announcements';
            liveRegion.setAttribute('aria-live', 'polite');
            liveRegion.setAttribute('aria-atomic', 'true');
            liveRegion.style.position = 'absolute';
            liveRegion.style.left = '-10000px';
            liveRegion.style.width = '1px';
            liveRegion.style.height = '1px';
            liveRegion.style.overflow = 'hidden';
            document.body.appendChild(liveRegion);
        }

        // Clear previous announcement and set new one
        liveRegion.textContent = '';
        setTimeout(() => {
            liveRegion.textContent = action;
        }, 100);
    },

    /**
     * Sets focus to the CKEditor formatting toolbar if available
     * This is specifically for the CKEditor rich text formatting toolbar,
     * not for any ACE code editor toolbar or other toolbars in the application
     * @param {object} ckeditorInstance - The CKEditor instance (if available)
     */
    focusToolbar(ckeditorInstance) {
        if (ckeditorInstance && window.CKEDITOR && window.CKEDITOR.instances) {
            // CKEditor has built-in toolbar navigation for rich text formatting
            try {
                // Try to find the first CKEditor toolbar button for formatting controls
                const toolbarElement = ckeditorInstance.container.$.querySelector('.cke_toolbar');
                if (toolbarElement) {
                    const firstButton = toolbarElement.querySelector('a, button, [tabindex="0"]');
                    if (firstButton) {
                        firstButton.focus();
                        this.announceAction(t('interactions.extendedText.toolbarFocused'));
                        return true;
                    }
                }
            } catch (e) {
                console.warn('Could not focus CKEditor toolbar:', e);
            }
        }
        return false;
    },

    /**
     * Returns focus to the main CKEditor editing area
     * This focuses back to the rich text content area, not any code editor
     * @param {object} ckeditorInstance - The CKEditor instance (if available)
     */
    returnToEditor(ckeditorInstance) {
        if (ckeditorInstance && ckeditorInstance.focus) {
            try {
                ckeditorInstance.focus();
                this.announceAction(t('interactions.extendedText.editorFocused'));
                return true;
            } catch (e) {
                console.warn('Could not focus CKEditor instance:', e);
            }
        }
        return false;
    },
};

export default extendedTextKeyboardService;
